import type { BrandProposalStatus } from '../api/payment/brand-deal.api';
import { validateContentFormat } from './brand-content-format';
import { countdownLabel } from './brand-creative';
import { isValidHttpsUrl } from './brand-deal';

// Đơn nhận đề xuất (spec 2026-10-12 §6) — câu và hàm thuần, câu lỗi khớp payment (BrandProposalConst/BrandProposalRules).

export const PROPOSAL_LIMITS = {
  titleMaxLength: 120,
  wishMinLength: 20,
  wishMaxLength: 2000,
  textMaxLength: 2000,
  maxImages: 10,
  maxVideos: 1,
  declineReasonMaxLength: 300,
} as const;

export const PROPOSAL_KIND_LABEL = 'Nhận đề xuất';

export const PROPOSAL_COMPOSER_NOTE =
  'Giá cố định theo giá suất của chiến dịch. Người đặt chỉ xem bản có đóng dấu và không tải được file gốc trước khi chọn. Đề xuất không được chọn vẫn là nội dung của bạn.';

export const PROPOSAL_ORDERER_NOTE = 'Bạn chỉ xem được bản có đóng dấu. File gốc chỉ dùng được sau khi bạn chọn đề xuất.';

export const PUBLISH_CONFIRM_TEXT = 'Đăng bài đã duyệt lên trang của bạn? Bài mang nhãn hợp tác và không xoá được trong 30 ngày.';

export const APPROVE_PROPOSAL_CONFIRM =
  'Duyệt bài này? Creator sẽ bấm Đăng công khai để kết thúc đơn, quá hạn hệ thống tự đăng đúng bản đã duyệt.';

export const isProposalKind = (kind?: string | null): boolean => kind === 'Proposal';

/** Creative và Proposal đi chung luồng nhiệm vụ (nộp, duyệt, yêu cầu sửa). */
export const usesTaskFlow = (kind?: string | null): boolean => kind === 'Creative' || kind === 'Proposal';

export const proposalStatusLabel = (s: BrandProposalStatus): string => {
  switch (s) {
    case 'Pending':
      return 'Đang chờ';
    case 'Accepted':
      return 'Đã được chọn';
    case 'Declined':
      return 'Chưa được chọn';
    case 'Withdrawn':
      return 'Đã rút';
    case 'Expired':
      return 'Hết hạn';
    default:
      return s;
  }
};

export const validateWish = (text: string): string | null => {
  const length = (text ?? '').trim().length;
  return length < PROPOSAL_LIMITS.wishMinLength || length > PROPOSAL_LIMITS.wishMaxLength
    ? 'Mô tả mong muốn từ 20 đến 2.000 ký tự.'
    : null;
};

export interface ProposalCampaignFormInput {
  title: string;
  wish: string;
  maxRevisions: number;
  images: number;
  destinationUrl?: string;
}

/** Cùng thứ tự BrandProposalRules.ValidateCampaign: tiêu đề ⇒ mô tả ⇒ số lần sửa ⇒ ảnh ⇒ link (định dạng kiểm riêng ở form). */
export const validateProposalCampaignForm = (input: ProposalCampaignFormInput): string | null => {
  if (!input.title.trim()) return 'Bạn chưa nhập tiêu đề.';
  if (input.title.length > PROPOSAL_LIMITS.titleMaxLength) return `Tiêu đề tối đa ${PROPOSAL_LIMITS.titleMaxLength} ký tự.`;
  const wishError = validateWish(input.wish);
  if (wishError) return wishError;
  if (!Number.isInteger(input.maxRevisions) || input.maxRevisions < 0 || input.maxRevisions > 3) return 'Số lần sửa phải từ 0 đến 3.';
  if (input.images > PROPOSAL_LIMITS.maxImages) return `Tối đa ${PROPOSAL_LIMITS.maxImages} ảnh.`;
  if (input.destinationUrl && input.destinationUrl.trim() && !isValidHttpsUrl(input.destinationUrl))
    return 'Link đích phải bắt đầu bằng https://.';
  return null;
};

/** Cùng thứ tự BrandProposalRules.ValidateContent: có nội dung ⇒ giới hạn bản nháp ⇒ định dạng chiến dịch. */
export const validateProposal = (format: string | null | undefined, text: string, images: number, videos: number): string | null => {
  const t = (text ?? '').trim();
  if (!t && images === 0 && videos === 0) return 'Đề xuất cần có nội dung.';
  if (t.length > PROPOSAL_LIMITS.textMaxLength) return 'Nội dung tối đa 2.000 ký tự.';
  if (images > PROPOSAL_LIMITS.maxImages || videos > PROPOSAL_LIMITS.maxVideos) return 'Tối đa 10 ảnh và 1 video.';
  return validateContentFormat(format, text, images, videos);
};

export const validateDeclineReason = (reason: string): string | null =>
  (reason ?? '').trim().length > PROPOSAL_LIMITS.declineReasonMaxLength ? 'Lý do từ chối tối đa 300 ký tự.' : null;

export const acceptConfirmText = (reviewDays: number): string =>
  `Chọn đề xuất này? Một suất được chuyển cho creator và bài vào hàng chờ duyệt ngay, bạn có ${reviewDays} ngày để duyệt.`;

export const publishCountdownText = (dueIso: string | null | undefined, nowMs: number): string => {
  const label = countdownLabel(dueIso, nowMs);
  if (!label) return '';
  return label === 'Đã quá hạn'
    ? 'Đã quá hạn, hệ thống sẽ tự đăng đúng bản đã duyệt trong ít phút.'
    : `${label}, quá hạn hệ thống sẽ tự đăng đúng bản đã duyệt.`;
};
